import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import DashboardLayout from '../../components/layout/DashboardLayout';
import SEO from '../../components/SEO';
import {
  Trophy,
  Target,
  Flame,
  Check,
  X,
  RotateCcw,
  ArrowRight,
  ArrowLeft,
  BookOpen,
  HelpCircle,
  AlertCircle,
  Star,
  Zap,
  GraduationCap,
  Medal,
  Lock,
  Unlock
} from 'lucide-react';
import { updateStreak } from '../../utils/streak';
import { logActivity } from '../../utils/activity';
import { encodeId, findByEncodedId } from '../../utils/ids';
import { trackAnswer } from '../../utils/wrongQuestions';
import { checkAndAwardBadges, Badge } from '../../utils/badges';
import {
  getUserPlanType,
  canAccessCategory,
  FREE_PLAN_LIMITS
} from '../../utils/subscription';
import { useTranslation } from 'react-i18next';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

type QuizMode = 'qcm' | 'vrai_faux' | null;

export default function Quiz() {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const categoryId = searchParams.get('category');

  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [quizMode, setQuizMode] = useState<QuizMode>(null);
  const [questions, setQuestions] = useState<any[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | boolean | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [answers, setAnswers] = useState<boolean[]>([]);
  const [showFinalScore, setShowFinalScore] = useState(false);
  const [subscription, setSubscription] = useState<any>(null);
  const [newBadges, setNewBadges] = useState<Badge[]>([]);
  const [showBadgeModal, setShowBadgeModal] = useState(false);
  const [categoryProgress, setCategoryProgress] = useState<any>(null);

  useEffect(() => {
    const init = async () => {
      await Promise.all([
        checkUser(),
        loadCategories()
      ]);
      setLoading(false);
    };
    init();
  }, []);

  useEffect(() => {
    if (!loading && categoryId && categories.length > 0) {
      const category = findByEncodedId(categories, categoryId);
      if (category) {
        checkCategoryAccess(category.id);
      } else {
        // Handle invalid UUID
        window.REACT_APP_NAVIGATE('/pricing');
      }
    }
  }, [categoryId, subscription, categories, loading]);

  useEffect(() => {
    if (selectedCategory && user) {
      fetchCategoryProgress(selectedCategory);
    }
  }, [selectedCategory, user]);

  useEffect(() => {
    if (!loading && !selectedCategory && categories.length > 0 && getUserPlanType(subscription) === 'free') {
      checkGlobalAccess();
    }
  }, [loading, selectedCategory, categories, subscription]);

  const checkGlobalAccess = async () => {
    if (!user) return;
    try {
      const { data: progress } = await supabase
        .from('user_progress')
        .select('qcm_completed, vrai_faux_completed')
        .eq('user_id', user.id)
        .eq('category_id', categories[FREE_PLAN_LIMITS.allowedCategoryIndex].id)
        .maybeSingle();

      if (progress &&
        progress.qcm_completed >= FREE_PLAN_LIMITS.maxQuiz &&
        progress.vrai_faux_completed >= FREE_PLAN_LIMITS.maxQuiz) {
        window.REACT_APP_NAVIGATE('/pricing');
      }
    } catch (error) {
      console.error('Error checking global access:', error);
    }
  };

  const fetchCategoryProgress = async (catId: number) => {
    try {
      const { data } = await supabase
        .from('user_progress')
        .select('*')
        .eq('user_id', user.id)
        .eq('category_id', catId)
        .maybeSingle();
      setCategoryProgress(data);
    } catch (error) {
      console.error('Error fetching category progress:', error);
    }
  };

  const checkUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        window.REACT_APP_NAVIGATE('/login');
        return;
      }

      setUser(user);
      await fetchSubscription(user.id);
    } catch (error) {
      console.error('Erreur:', error);
      window.REACT_APP_NAVIGATE('/login');
    }
  };

  const fetchSubscription = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'active')
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching subscription:', error);
      }

      setSubscription(data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const checkCategoryAccess = async (catId: number) => {
    if (categories.length === 0) return; // Wait for categories to load

    const categoryIndex = categories.findIndex(c => c.id === catId);

    // If category not found, or user is not allowed to access this category based on their plan
    if (categoryIndex === -1 || !canAccessCategory(subscription, categoryIndex)) {
      window.REACT_APP_NAVIGATE('/pricing');
      return;
    }

    // Addition: Check if free user has already completed everything in this category
    if (getUserPlanType(subscription) === 'free') {
      try {
        const { data: progress } = await supabase
          .from('user_progress')
          .select('qcm_completed, vrai_faux_completed')
          .eq('user_id', user.id)
          .eq('category_id', catId)
          .maybeSingle();

        if (progress &&
          progress.qcm_completed >= FREE_PLAN_LIMITS.maxQuiz &&
          progress.vrai_faux_completed >= FREE_PLAN_LIMITS.maxQuiz) {
          window.REACT_APP_NAVIGATE('/pricing');
          return;
        }
      } catch (error) {
        console.error('Error checking progress:', error);
      }
    }

    setSelectedCategory(catId);

    // Check if we should automatically start in retry mode
    if (searchParams.get('mode') === 'retry') {
      const type = searchParams.get('type') as QuizMode;
      if (type) startQuiz(type, catId, true);
    }
  };

  const loadCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('id', { ascending: true });

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const startQuiz = async (mode: QuizMode, catId: number, isRetry: boolean = false) => {
    if (!mode) return;

    // Check if free user has already completed the limit for this SPECIFIC mode
    if (getUserPlanType(subscription) === 'free' && !isRetry) {
      try {
        const { data: progress } = await supabase
          .from('user_progress')
          .select('qcm_completed, vrai_faux_completed')
          .eq('user_id', user.id)
          .eq('category_id', catId)
          .maybeSingle();

        const completed = mode === 'qcm' ? progress?.qcm_completed : progress?.vrai_faux_completed;
        if (completed && completed >= FREE_PLAN_LIMITS.maxQuiz) {
          window.REACT_APP_NAVIGATE('/pricing');
          return;
        }
      } catch (error) {
        console.error('Error checking quiz progress:', error);
      }
    }

    try {
      let query = supabase
        .from(mode === 'qcm' ? 'qcm' : 'vrai_faux')
        .select('*')
        .eq('category_id', catId);

      if (isRetry) {
        const wrongIds = (user?.user_metadata?.wrong_questions || [])
          .filter((q: any) => q.categoryId === catId && q.type === mode)
          .map((q: any) => q.id);

        if (wrongIds.length > 0) {
          query = query.in('id', wrongIds);
        } else {
          // No wrong questions found for this mode/category
          setQuestions([]);
          setQuizMode(mode);
          return;
        }
      }

      const { data, error } = await query;
      if (error) throw error;

      // Shuffle questions for random order
      let shuffled = (data || []).sort(() => Math.random() - 0.5);

      // Apply Free Plan Limits
      const planType = getUserPlanType(subscription);
      if (planType === 'free') {
        shuffled = shuffled.slice(0, FREE_PLAN_LIMITS.maxQuiz);
      }

      setQuestions(shuffled);
      setQuizMode(mode);

      // Check for existing progress to resume
      const { data: progress } = await supabase
        .from('user_progress')
        .select('*')
        .eq('user_id', user.id)
        .eq('category_id', catId)
        .maybeSingle();

      const field = mode === 'qcm' ? 'qcm_completed' : 'vrai_faux_completed';
      const resumeIndex = progress && progress[field] < (data?.length || 0) ? progress[field] : 0;

      setCurrentIndex(resumeIndex);
      setSelectedAnswer(null);
      setShowResult(false);
      setScore(0);
      setAnswers([]);
      setShowFinalScore(false);
    } catch (error) {
      console.error('Erreur:', error);
      setQuestions([]);
      setQuizMode(mode);
    }
  };

  const handleAnswerSelect = (answer: number | boolean) => {
    if (showResult) return;
    setSelectedAnswer(answer);
  };

  const handleSubmitAnswer = async () => {
    if (selectedAnswer === null) return;

    const currentQuestion = questions[currentIndex];
    let isCorrect = false;

    if (quizMode === 'qcm') {
      isCorrect = selectedAnswer === currentQuestion.correct_index;
    } else if (quizMode === 'vrai_faux') {
      isCorrect = selectedAnswer === currentQuestion.answer;
    }

    if (isCorrect) {
      setScore(score + 1);
    }

    setAnswers([...answers, isCorrect]);
    setShowResult(true);

    // Track wrong question for targeted reinforcement
    const updatedUser = await trackAnswer(currentQuestion.id, selectedCategory!, quizMode!, isCorrect);
    if (updatedUser) setUser(updatedUser);

    // Save incremental progress - pass current Index to be thread-safe
    await updateProgress(isCorrect, currentIndex);

    // Log activity on every question to show real-time progress
    const category = categories.find(c => c.id === selectedCategory);
    logActivity({
      type: 'quiz',
      title: (i18n.language === 'en' ? category?.title_en : category?.title) || category?.title || 'Quiz',
      subtitle: quizMode === 'qcm' ? t('quiz.mode.qcm.title') : t('quiz.mode.vrai_faux.title'),
      value: `${currentIndex + 1}/${questions.length}`,
      categoryId: selectedCategory as number
    });
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedAnswer(null);
      setShowResult(false);
    } else {
      setShowFinalScore(true);
      // Log activity when quiz is finished
      const category = categories.find(c => c.id === selectedCategory);
      logActivity({
        type: 'quiz',
        title: (i18n.language === 'en' ? category?.title_en : category?.title) || category?.title || 'Quiz',
        subtitle: quizMode === 'qcm' ? t('quiz.mode.qcm.title') : t('quiz.mode.vrai_faux.title'),
        value: `${score}/${questions.length}`,
        categoryId: selectedCategory as number
      });

      // Check for badges when quiz is finished
      checkForBadges();
    }
  };

  const checkForBadges = async () => {
    if (!user || !selectedCategory) return;

    try {
      const { data: allProgress } = await supabase
        .from('user_progress')
        .select('*')
        .eq('user_id', user.id);

      const currentProgress = allProgress?.find(p => p.category_id === selectedCategory);

      // Calculate category specific stats
      const counts = await getCategoryCounts(selectedCategory);
      const quizCompleted = (currentProgress?.qcm_completed || 0) + (currentProgress?.vrai_faux_completed || 0);
      const quizTotal = (counts.qcm || 0) + (counts.vrai_faux || 0);
      const progressPercent = quizTotal > 0 ? Math.round((quizCompleted / quizTotal) * 100) : 0;

      // Count total fully completed themes
      let themesCompleted = 0;
      for (const cat of categories) {
        const p = allProgress?.find(pr => pr.category_id === cat.id);
        if (p) {
          const c = await getCategoryCounts(cat.id);
          const qComp = (p.qcm_completed || 0) + (p.vrai_faux_completed || 0);
          const qTot = (c.qcm || 0) + (c.vrai_faux || 0);
          if (qTot > 0 && qComp >= qTot) themesCompleted++;
        }
      }

      const awarded = await checkAndAwardBadges(user.id, selectedCategory, {
        score,
        total: questions.length,
        progressPercent,
        totalThemesCompleted: themesCompleted
      });

      if (awarded.length > 0) {
        setNewBadges(awarded);
        setShowBadgeModal(true);
      }
    } catch (error) {
      console.error('Error checking badges:', error);
    }
  };

  const getCategoryCounts = async (catId: number) => {
    const [qcm, vraiFaux] = await Promise.all([
      supabase.from('qcm').select('id', { count: 'exact', head: true }).eq('category_id', catId),
      supabase.from('vrai_faux').select('id', { count: 'exact', head: true }).eq('category_id', catId)
    ]);
    return {
      qcm: qcm.count || 0,
      vrai_faux: vraiFaux.count || 0
    };
  };

  const updateProgress = async (isCorrect?: boolean, forcedIndex?: number) => {
    if (!user || !selectedCategory || !quizMode) return;

    try {
      const { data: existing, error: fetchError } = await supabase
        .from('user_progress')
        .select('*')
        .eq('user_id', user.id)
        .eq('category_id', selectedCategory)
        .maybeSingle();

      if (fetchError) throw fetchError;

      const field = quizMode === 'qcm' ? 'qcm_completed' : 'vrai_faux_completed';
      const indexToUse = forcedIndex !== undefined ? forcedIndex : currentIndex;
      const currentCompleted = indexToUse + 1;

      if (existing) {
        const { error: updateError } = await supabase
          .from('user_progress')
          .update({
            [field]: Math.max(existing[field] || 0, currentCompleted),
            total_score: (existing.total_score || 0) + (isCorrect ? 1 : 0),
            last_activity: new Date().toISOString()
          })
          .eq('user_id', user.id)
          .eq('category_id', selectedCategory);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('user_progress')
          .insert({
            user_id: user.id,
            category_id: selectedCategory,
            flashcards_completed: 0,
            qcm_completed: quizMode === 'qcm' ? currentCompleted : 0,
            vrai_faux_completed: quizMode === 'vrai_faux' ? currentCompleted : 0,
            total_score: isCorrect ? 1 : 0,
            last_activity: new Date().toISOString()
          });

        if (insertError) throw insertError;
      }

      await updateStreak();
    } catch (error) {
      console.error('Erreur mise à jour:', error);
    }
  };

  const resetQuiz = () => {
    setQuizMode(null);
    setQuestions([]);
    setCurrentIndex(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setScore(0);
    setAnswers([]);
    setShowFinalScore(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const progressPercent = questions.length > 0 ? Math.round(((currentIndex + 1) / questions.length) * 100) : 0;

  return (
    <DashboardLayout user={user}>
      <SEO noindex={true} />
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-text-main mb-2 flex items-center gap-2">
            {t('quiz.title')} <span className="text-primary">{t('quiz.title_highlight')}</span>
          </h1>
          <p className="text-text-muted">{t('quiz.subtitle')}</p>
        </div>

        {/* Category Selection */}
        {!selectedCategory && (
          <div className="grid md:grid-cols-3 gap-6">
            {categories.map((category, index) => {
              const isLocked = !canAccessCategory(subscription, index);
              const title = (i18n.language === 'en' ? category.title_en : category.title) || category.title;
              const description = (i18n.language === 'en' ? category.description_en : category.description) || category.description;

              return (
                <button
                  key={category.id}
                  onClick={() => isLocked ? window.REACT_APP_NAVIGATE('/pricing') : setSelectedCategory(category.id)}
                  className={`bg-dark-card rounded-2xl border border-dark-border overflow-hidden transition-all text-left group ${isLocked ? 'opacity-75' : 'hover:border-primary/50'}`}
                >
                  <div className="h-32 bg-dark-bg relative flex items-center justify-center border-b border-dark-border group-hover:bg-dark-hover transition-colors">
                    <span className="text-4xl font-bold text-primary opacity-20 group-hover:opacity-40 transition-opacity">
                      {index + 1}
                    </span>
                    {isLocked && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                        <div className="w-12 h-12 bg-dark-card rounded-full flex items-center justify-center border border-dark-border">
                          <AlertCircle className="text-text-muted" />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="p-6">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-lg font-bold text-text-main group-hover:text-primary transition-colors">{title}</h3>
                      {isLocked && (
                        <span className="px-2 py-0.5 bg-red-500/10 text-red-600 text-xs font-bold rounded border border-red-500/20">
                          PRO
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-text-muted">{description}</p>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Quiz Mode Selection */}
        {selectedCategory && !quizMode && (
          <div>
            <div className="mb-6">
              <button
                onClick={() => setSelectedCategory(null)}
                className="flex items-center gap-2 text-text-muted hover:text-text-main transition-colors"
              >
                <ArrowLeft size={20} />
                {t('quiz.nav.back_themes')}
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {getUserPlanType(subscription) === 'free' && (categoryProgress?.qcm_completed || 0) >= FREE_PLAN_LIMITS.maxQuiz ? (
                <button
                  onClick={() => window.REACT_APP_NAVIGATE('/pricing')}
                  className="bg-dark-card rounded-2xl border border-red-500/30 p-8 hover:bg-red-500/5 transition-all text-left flex flex-col justify-between"
                >
                  <div>
                    <div className="w-16 h-16 bg-red-500/10 rounded-xl flex items-center justify-center mb-6">
                      <Lock className="text-red-500" size={32} />
                    </div>
                    <h3 className="text-xl font-bold text-text-main mb-2">{t('quiz.mode.qcm.title')} <span className="text-xs bg-red-500/20 text-red-500 px-2 py-0.5 rounded ml-2 uppercase">{t('quiz.mode.locked_pro')}</span></h3>
                    <p className="text-text-muted">{t('quiz.mode.limit_reached', { count: FREE_PLAN_LIMITS.maxQuiz })}</p>
                  </div>
                  <div className="mt-6 pt-6 border-t border-dark-border text-primary font-bold">
                    {t('quiz.mode.upgrade_pro')}
                  </div>
                </button>
              ) : (
                <button
                  onClick={() => startQuiz('qcm', selectedCategory)}
                  className="bg-dark-card rounded-2xl border border-dark-border p-8 hover:bg-dark-hover hover:border-primary/50 transition-all text-left group"
                >
                  <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <BookOpen className="text-primary" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-text-main mb-2">{t('quiz.mode.qcm.title')}</h3>
                  <p className="text-text-muted">{t('quiz.mode.qcm.description')}</p>
                </button>
              )}

              {getUserPlanType(subscription) === 'free' && (categoryProgress?.vrai_faux_completed || 0) >= FREE_PLAN_LIMITS.maxQuiz ? (
                <button
                  onClick={() => window.REACT_APP_NAVIGATE('/pricing')}
                  className="bg-dark-card rounded-2xl border border-red-500/30 p-8 hover:bg-red-500/5 transition-all text-left flex flex-col justify-between"
                >
                  <div>
                    <div className="w-16 h-16 bg-red-500/10 rounded-xl flex items-center justify-center mb-6">
                      <Lock className="text-red-500" size={32} />
                    </div>
                    <h3 className="text-xl font-bold text-text-main mb-2">{t('quiz.mode.vrai_faux.title')} <span className="text-xs bg-red-500/20 text-red-500 px-2 py-0.5 rounded ml-2 uppercase">{t('quiz.mode.locked_pro')}</span></h3>
                    <p className="text-text-muted">{t('quiz.mode.limit_reached', { count: FREE_PLAN_LIMITS.maxQuiz })}</p>
                  </div>
                  <div className="mt-6 pt-6 border-t border-dark-border text-primary font-bold">
                    {t('quiz.mode.upgrade_pro')}
                  </div>
                </button>
              ) : (
                <button
                  onClick={() => startQuiz('vrai_faux', selectedCategory)}
                  className="bg-dark-card rounded-2xl border border-dark-border p-8 hover:bg-dark-hover hover:border-primary/50 transition-all text-left group"
                >
                  <div className="w-16 h-16 bg-blue-500/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <Check className="text-blue-500" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-text-main mb-2">{t('quiz.mode.vrai_faux.title')}</h3>
                  <p className="text-text-muted">{t('quiz.mode.vrai_faux.description')}</p>
                </button>
              )}
            </div>

            {/* Wrong Questions Section */}
            {((getUserPlanType(subscription) !== 'free') ||
              ((categoryProgress?.qcm_completed || 0) < FREE_PLAN_LIMITS.maxQuiz ||
                (categoryProgress?.vrai_faux_completed || 0) < FREE_PLAN_LIMITS.maxQuiz)) &&
              (user?.user_metadata?.wrong_questions || []).filter((q: any) => q.categoryId === selectedCategory).length > 0 && (
                <div className="mt-12 p-8 bg-primary/5 rounded-3xl border border-primary/20">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                      <h3 className="text-xl font-bold text-text-main mb-2 flex items-center gap-2">
                        <AlertCircle className="text-primary" size={24} />
                        {t('quiz.retry_errors.title')}
                      </h3>
                      <p className="text-text-muted">
                        {t('quiz.retry_errors.subtitle', { count: (user?.user_metadata?.wrong_questions || []).filter((q: any) => q.categoryId === selectedCategory).length })}
                      </p>
                    </div>
                    <div className="flex gap-4">
                      {(user?.user_metadata?.wrong_questions || []).filter((q: any) => q.categoryId === selectedCategory && q.type === 'qcm').length > 0 && (
                        <button
                          onClick={() => startQuiz('qcm', selectedCategory, true)}
                          className="px-6 py-3 bg-primary/10 text-primary font-bold rounded-xl hover:bg-primary/20 transition-all border border-primary/20"
                        >
                          {t('quiz.retry_errors.qcm')}
                        </button>
                      )}
                      {(user?.user_metadata?.wrong_questions || []).filter((q: any) => q.categoryId === selectedCategory && q.type === 'vrai_faux').length > 0 && (
                        <button
                          onClick={() => startQuiz('vrai_faux', selectedCategory, true)}
                          className="px-6 py-3 bg-blue-500/10 text-blue-500 font-bold rounded-xl hover:bg-blue-500/20 transition-all border border-blue-500/20"
                        >
                          {t('quiz.retry_errors.vrai_faux')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
          </div>
        )}

        {/* Quiz Questions */}
        {quizMode && !showFinalScore && (
          <div>
            {questions.length === 0 ? (
              <div className="bg-dark-card rounded-2xl border border-dark-border p-12 text-center">
                <HelpCircle className="mx-auto text-text-muted mb-4" size={48} />
                <h2 className="text-xl font-bold text-text-main mb-2">{t('quiz.status.no_questions')}</h2>
                <p className="text-text-muted mb-6">{t('quiz.status.no_questions_desc')}</p>
                <div className="flex gap-4 justify-center">
                  <button onClick={resetQuiz} className="px-6 py-2 bg-dark-hover text-white rounded-xl">{t('quiz.nav.retry')}</button>
                </div>
              </div>
            ) : currentQuestion && (
              <>
                {/* Header Info */}
                <div className="flex items-center justify-between mb-6">
                  <button onClick={resetQuiz} className="text-text-muted hover:text-text-main flex items-center gap-2 text-sm">
                    <X size={16} /> {t('quiz.nav.quit')}
                  </button>
                  <div className="flex items-center gap-4">
                    <span className="px-3 py-1 bg-dark-card rounded-lg border border-dark-border text-xs text-text-muted">
                      {t('quiz.status.question', { current: currentIndex + 1, total: questions.length })}
                    </span>
                    <span className="px-3 py-1 bg-primary/10 rounded-lg border border-primary/20 text-xs text-primary font-bold">
                      {t('quiz.status.score', { score: score })}
                    </span>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="w-full h-1 bg-dark-card rounded-full mb-8 overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-300 ease-out"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>

                {/* Question Card */}
                <div className="bg-dark-card rounded-2xl border border-dark-border p-8 mb-6">
                  <h3 className="text-2xl font-bold text-text-main mb-8 leading-relaxed">
                    {quizMode === 'qcm'
                      ? (i18n.language === 'en' ? currentQuestion.question_en : currentQuestion.question) || currentQuestion.question
                      : (i18n.language === 'en' ? currentQuestion.statement_en : currentQuestion.statement) || currentQuestion.statement
                    }
                  </h3>

                  {/* QCM Options */}
                  {quizMode === 'qcm' && (
                    <div className="space-y-3">
                      {['option_a', 'option_b', 'option_c', 'option_d'].map((option, index) => {
                        const isSelected = selectedAnswer === index;
                        const isCorrect = currentQuestion.correct_index === index;
                        const showCorrect = showResult && isCorrect;
                        const showWrong = showResult && isSelected && !isCorrect;

                        const optionKeyEn = `${option}_en`;
                        const optionText = (i18n.language === 'en' ? currentQuestion[optionKeyEn] : currentQuestion[option]) || currentQuestion[option];

                        return (
                          <button
                            key={option}
                            onClick={() => handleAnswerSelect(index)}
                            disabled={showResult}
                            className={`w-full p-4 rounded-xl border text-left transition-all flex items-center gap-4 group ${showCorrect
                              ? 'border-green-500 bg-green-500/10'
                              : showWrong
                                ? 'border-red-500 bg-red-500/10'
                                : isSelected
                                  ? 'border-primary bg-primary/10'
                                  : 'border-dark-border bg-dark-bg hover:bg-dark-hover hover:border-dark-hover'
                              } ${showResult ? 'cursor-default' : 'cursor-pointer'}`}
                          >
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold border transition-colors ${showCorrect
                              ? 'bg-green-500 border-green-500 text-black'
                              : showWrong
                                ? 'bg-red-500 border-red-500 text-white'
                                : isSelected
                                  ? 'bg-primary border-primary text-black'
                                  : 'bg-dark-card border-dark-border text-text-muted group-hover:border-text-muted'
                              }`}>
                              {String.fromCharCode(65 + index)}
                            </div>
                            <span className={`flex-1 ${isSelected || showCorrect ? 'text-text-main' : 'text-text-muted group-hover:text-text-main'}`}>
                              {optionText}
                            </span>
                            {showCorrect && <Check className="text-green-500" size={20} />}
                            {showWrong && <X className="text-red-500" size={20} />}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Vrai/Faux Options */}
                  {quizMode === 'vrai_faux' && (
                    <div className="grid grid-cols-2 gap-4">
                      {[
                        { value: true, label: t('quiz.vrai_faux.true'), icon: Check },
                        { value: false, label: t('quiz.vrai_faux.false'), icon: X }
                      ].map((option) => {
                        const isSelected = selectedAnswer === option.value;
                        const isCorrect = currentQuestion.answer === option.value;
                        const showCorrect = showResult && isCorrect;
                        const showWrong = showResult && isSelected && !isCorrect;
                        const Icon = option.icon;

                        return (
                          <button
                            key={option.label}
                            onClick={() => handleAnswerSelect(option.value)}
                            disabled={showResult}
                            className={`p-6 rounded-xl border-2 transition-all flex flex-col items-center gap-4 ${showCorrect
                              ? 'border-green-500 bg-green-500/10'
                              : showWrong
                                ? 'border-red-500 bg-red-500/10'
                                : isSelected
                                  ? 'border-primary bg-primary/10'
                                  : 'border-dark-border bg-dark-bg hover:border-primary/50'
                              }`}
                          >
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${showCorrect ? 'bg-green-500 text-black' : isSelected ? 'bg-primary text-black' : 'bg-dark-card text-text-muted'
                              }`}>
                              <Icon size={24} />
                            </div>
                            <span className={`text-lg font-bold ${isSelected || showCorrect ? 'text-text-main' : 'text-text-muted'}`}>
                              {option.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Explanation */}
                  {showResult && (currentQuestion.explication || currentQuestion.explication_en) && (
                    <div className={`mt-6 p-4 rounded-xl border ${answers[answers.length - 1]
                      ? 'bg-green-500/10 border-green-500/20'
                      : 'bg-red-500/10 border-red-500/20'
                      }`}>
                      <div className="flex items-start gap-3">
                        <HelpCircle className={answers[answers.length - 1] ? 'text-green-500' : 'text-red-500'} size={20} />
                        <div>
                          <p className={`text-sm font-bold mb-1 ${answers[answers.length - 1] ? 'text-green-500' : 'text-red-500'}`}>
                            {answers[answers.length - 1] ? t('quiz.feedback.correct') : t('quiz.feedback.incorrect')}
                          </p>
                          <p className="text-sm text-text-main leading-relaxed">
                            <span className="font-bold">{t('quiz.feedback.explanation')}:</span> {
                              (i18n.language === 'en' ? currentQuestion.explication_en : currentQuestion.explication) || currentQuestion.explication
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex justify-between items-center">
                  {!showResult ? (
                    <button
                      onClick={handleSubmitAnswer}
                      disabled={selectedAnswer === null}
                      className="ml-auto flex items-center gap-2 px-8 py-4 bg-primary text-white font-bold rounded-xl hover:bg-primary-hover transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-primary/20"
                    >
                      {t('quiz.nav.submit')}
                      <ArrowRight size={20} />
                    </button>
                  ) : (
                    <button
                      onClick={handleNext}
                      className="ml-auto flex items-center gap-2 px-8 py-4 bg-primary text-white font-bold rounded-xl hover:bg-primary-hover transition-all shadow-lg shadow-primary/20"
                    >
                      {currentIndex < questions.length - 1 ? t('quiz.nav.next') : t('quiz.nav.finish')}
                      <ArrowRight size={20} />
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Final Results */}
        {showFinalScore && (
          <div className="bg-dark-card rounded-3xl border border-dark-border p-12 text-center shadow-2xl relative overflow-hidden">
            {/* Background Decorations */}
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-primary via-blue-500 to-primary" />
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-primary/10 rounded-full blur-3xl" />
            <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl" />

            {/* Trophy Icon */}
            <div className="relative mb-8">
              <div className="w-24 h-24 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto rotate-12 group-hover:rotate-0 transition-transform">
                <Trophy className="text-primary" size={48} />
              </div>
              <div className="absolute -top-2 right-1/2 translate-x-12">
                <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center border-4 border-dark-card animate-bounce">
                  <Star className="text-white" size={12} fill="currentColor" />
                </div>
              </div>
            </div>

            <h2 className="text-4xl font-bold text-text-main mb-2">{t('quiz.result.title')}</h2>
            <p className="text-text-muted mb-8 text-lg">
              {score === questions.length ? t('quiz.result.perfect') :
                score > questions.length / 2 ? t('quiz.result.good') :
                  score > 0 ? t('quiz.result.average') : t('quiz.result.bad')}
            </p>

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-6 mb-12">
              <div className="p-4 bg-dark-bg rounded-2xl border border-dark-border">
                <p className="text-xs text-text-muted uppercase font-bold tracking-wider mb-1">{t('quiz.result.stats.questions')}</p>
                <p className="text-2xl font-bold text-text-main">{questions.length}</p>
              </div>
              <div className="p-4 bg-dark-bg rounded-2xl border border-dark-border">
                <p className="text-xs text-text-muted uppercase font-bold tracking-wider mb-1">{t('quiz.result.stats.correct')}</p>
                <p className="text-2xl font-bold text-green-500">{score}</p>
              </div>
              <div className="p-4 bg-dark-bg rounded-2xl border border-dark-border">
                <p className="text-xs text-text-muted uppercase font-bold tracking-wider mb-1">{t('quiz.result.stats.accuracy')}</p>
                <p className="text-2xl font-bold text-primary">{Math.round((score / (questions.length || 1)) * 100)}%</p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-4 justify-center">
              <button
                onClick={resetQuiz}
                className="flex items-center justify-center gap-2 px-8 py-4 bg-primary text-white font-bold rounded-2xl hover:bg-primary-hover transition-all shadow-lg shadow-primary/20"
              >
                <RotateCcw size={20} />
                {t('quiz.nav.retry')}
              </button>
              <button
                onClick={() => setSelectedCategory(null)}
                className="flex items-center justify-center gap-2 px-8 py-4 bg-dark-bg border border-dark-border text-text-main font-bold rounded-2xl hover:bg-dark-hover transition-all"
              >
                {t('quiz.nav.finish_session')}
              </button>
            </div>
          </div>
        )}

        {/* Badge Modal */}
        {showBadgeModal && newBadges.length > 0 && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="bg-dark-card border border-dark-border w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300">
              <div className="p-8 text-center">
                <div className="relative inline-block mb-6">
                  <div className="w-24 h-24 bg-primary/10 rounded-3xl flex items-center justify-center rotate-12 scale-110">
                    <Medal className="text-primary" size={56} />
                  </div>
                  <div className="absolute -top-4 -right-4">
                    <Zap className="text-yellow-500" size={32} fill="currentColor" />
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-text-main mb-2">{t('quiz.badges.new')}</h3>
                <p className="text-text-muted mb-8">{t('quiz.badges.congrats')}</p>

                <div className="space-y-4 mb-8">
                  {newBadges.map((badge, idx) => (
                    <div key={idx} className="p-4 bg-dark-bg rounded-2xl border border-dark-border flex items-center gap-4 text-left">
                      <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Trophy className="text-primary" size={24} />
                      </div>
                      <div>
                        <h4 className="font-bold text-text-main">
                          {(i18n.language === 'en' ? badge.title_en : badge.title) || badge.title}
                        </h4>
                        <p className="text-xs text-text-muted">
                          {(i18n.language === 'en' ? badge.description_en : badge.description) || badge.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => setShowBadgeModal(false)}
                  className="w-full py-4 bg-primary text-white font-bold rounded-2xl hover:bg-primary-hover transition-all shadow-lg shadow-primary/20"
                >
                  {t('quiz.badges.close')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
